import { clsx } from "clsx"
import { Link, useLocation } from "react-router-dom"

import useBatteryConcertApplication from "~/api/hooks/useBatteryConcertApplication"
import { Drawer } from "~/components/ui-components/v2"

import { APPLICATION_STATUS_ACTION_LABEL } from "../financing-status/application-status-labels"
import {
  CONCERT_EYEBROW,
  CONCERT_STAGES,
  concertStage,
} from "../financing-status/concert/concert-battery-copy"
import {
  ConcertSection,
  concertPrimaryButtonClass,
} from "../financing-status/concert/concert-battery-section"
import { ConcertBatteryApplicationPanel } from "../financing-status/concert/ConcertBatteryApplicationPanel"
import { Eyebrow } from "../primitives"

/**
 * Contract → Status for a Concert Battery plan. Same route, drawer chrome,
 * and Close behaviour as the Cash contract drawer; the body is the Concert
 * application status instead of DocuSign readiness.
 */
export const ConcertBatteryStatusDrawer = ({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  // Shares the panel's cached read; the progress strip belongs in the header
  // band, the way the Cash contract drawer carries its own.
  const { state } = useLocation()
  const application =
    useBatteryConcertApplication(proposalId).data?.application ?? null
  const stage = application ? concertStage(application) : 0

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title={APPLICATION_STATUS_ACTION_LABEL}
      widthClass="sm:max-w-xl"
      hideCloseButton
      contentClassName="flex flex-col gap-4 pb-0"
      data-testid="battery-concert-status-drawer"
      header={({ close }) => (
        <div className="border-b border-bp-border bg-bp-base px-6 pt-5 pb-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Eyebrow className="mb-1">{CONCERT_EYEBROW}</Eyebrow>
              <h2 className="font-display text-[length:var(--step-2)] font-semibold text-bp-text">
                {APPLICATION_STATUS_ACTION_LABEL}
              </h2>
              <p className="mt-1 text-[length:var(--step--1)] text-bp-text-muted">
                Existing Loan Application, approved terms, and supporting
                documents. Closing this drawer changes nothing.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              data-testid="battery-concert-status-close"
              className="rounded-md px-2 py-1 text-[length:var(--step--2)] font-semibold text-bp-text-muted hover:text-bp-text"
            >
              Close
            </button>
          </div>
          <ol
            className="mt-5 grid grid-cols-3 gap-2"
            aria-label="Application progress"
          >
            {CONCERT_STAGES.map((label, index) => (
              <li key={label} className="flex flex-col gap-1.5">
                <span
                  className={clsx(
                    `h-1 rounded-full`,
                    index < stage
                      ? `bg-bp-success`
                      : index === stage
                        ? `bg-bp-accent`
                        : `bg-bp-border-strong`,
                  )}
                />
                <span
                  className={clsx(
                    `text-[length:var(--step--2)] font-semibold`,
                    index === stage ? `text-bp-text` : `text-bp-text-dim`,
                  )}
                >
                  {label}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    >
      <ConcertSection
        title="Powur installation contract"
        testId="battery-concert-installation-contract"
      >
        <div className="flex flex-col items-start gap-3 px-4 py-4">
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            Concert’s loan information is separate from your installation
            contract. Send the Powur contract and applicable supplemental
            documents for signature here.
          </p>
          <Link
            to={`?package=installation`}
            state={state}
            className={concertPrimaryButtonClass}
          >
            Open Powur installation contract
          </Link>
        </div>
      </ConcertSection>
      <ConcertBatteryApplicationPanel proposalId={proposalId} />
    </Drawer>
  )
}
